import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { ITour } from 'app/shared/model/tour.model';
import { getEntities as getTours } from 'app/entities/tour/tour.reducer';
import { IPlace } from 'app/shared/model/place.model';
import { getEntities as getPlaces } from 'app/entities/place/place.reducer';
import { ITourStep } from 'app/shared/model/tour-step.model';
import { DurationMeasure } from 'app/shared/model/enumerations/duration-measure.model';
import { getEntity, updateEntity, createEntity, reset } from './tour-step.reducer';

export const TourStepUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const account = useAppSelector(state => state.authentication.account);
  const tours = useAppSelector(state => state.tour.entities);
  const places = useAppSelector(state => state.place.entities);
  const tourStepEntity = useAppSelector(state => state.tourStep.entity);
  const loading = useAppSelector(state => state.tourStep.loading);
  const updating = useAppSelector(state => state.tourStep.updating);
  const updateSuccess = useAppSelector(state => state.tourStep.updateSuccess);
  const durationMeasureValues = Object.keys(DurationMeasure);

  const [formData, setFormData] = useState<any>({
    createdBy: account?.id,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/tour-step' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({
        createdBy: account?.id,
        waitTimeMeasure: 'MINUTES',
        driveTimeMeasure: 'MINUTES',
        enabled: true,
      });
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getTours({}));
    dispatch(getPlaces({}));
  }, []);

  useEffect(() => {
    if (tourStepEntity) {
      setFormData({
        ...tourStepEntity,
        createdBy: tourStepEntity?.createdBy?.id,
        tour: tourStepEntity?.tour?.id,
        place: tourStepEntity?.place?.id,
      });
    }
  }, [tourStepEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    if (!formData.tour) {
      newErrors.tour = 'Tour is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.checked });
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };

    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.stepOrder !== undefined && typeof values.stepOrder !== 'number') {
      values.stepOrder = Number(values.stepOrder);
    }
    if (values.waitTime !== undefined && typeof values.waitTime !== 'number') {
      values.waitTime = Number(values.waitTime);
    }
    if (values.driveTime !== undefined && typeof values.driveTime !== 'number') {
      values.driveTime = Number(values.driveTime);
    }

    values.enabled = values.enabled === true;
    values.tour = values.tour || null;
    values.place = values.place || null;
    values.waitTimeMeasure = values.waitTimeMeasure || null;
    values.driveTimeMeasure = values.driveTimeMeasure || null;

    const entity = {
      ...tourStepEntity,
      ...values,
      createdBy: users.find(it => it.id.toString() === values.createdBy?.toString()),
      tour: tours.find(it => it.id.toString() === values.tour?.toString()),
      place: places.find(it => it.id.toString() === values.place?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.tourStep.home.createOrEditLabel">Create or edit a TourStep</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
            {!isNew ? (
              <TextField id="tour-step-id" name="id" label={translate('global.field.id')} value={formData.id || ''} fullWidth InputProps={{ readOnly: true }} />
            ) : null}
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourStep.code')}
              id="tour-step-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <FormControlLabel
              control={
                <Switch
                  id="tour-step-enabled"
                  name="enabled"
                  data-cy="enabled"
                  checked={formData.enabled || false}
                  onChange={handleSwitchChange('enabled')}
                />
              }
              label={translate('jhipsterSampleApplicationApp.tourStep.enabled')}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourStep.icon')}
              id="tour-step-icon"
              name="icon"
              data-cy="icon"
              value={formData.icon || ''}
              onChange={handleChange('icon')}
              fullWidth
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourStep.stepOrder')}
              id="tour-step-stepOrder"
              name="stepOrder"
              data-cy="stepOrder"
              value={formData.stepOrder || ''}
              onChange={handleChange('stepOrder')}
              fullWidth
              type="number"
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourStep.waitTime')}
              id="tour-step-waitTime"
              name="waitTime"
              data-cy="waitTime"
              value={formData.waitTime || ''}
              onChange={handleChange('waitTime')}
              fullWidth
              type="number"
            />
            <FormControl fullWidth>
              <InputLabel id="tour-step-waitTimeMeasure-label">
                {translate('jhipsterSampleApplicationApp.tourStep.waitTimeMeasure')}
              </InputLabel>
              <Select
                labelId="tour-step-waitTimeMeasure-label"
                id="tour-step-waitTimeMeasure"
                name="waitTimeMeasure"
                data-cy="waitTimeMeasure"
                value={formData.waitTimeMeasure || 'MINUTES'}
                label={translate('jhipsterSampleApplicationApp.tourStep.waitTimeMeasure')}
                onChange={handleSelectChange('waitTimeMeasure')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {durationMeasureValues.map(durationMeasure => (
                  <MenuItem value={durationMeasure} key={durationMeasure}>
                    {translate('jhipsterSampleApplicationApp.DurationMeasure.' + durationMeasure)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourStep.driveTime')}
              id="tour-step-driveTime"
              name="driveTime"
              data-cy="driveTime"
              value={formData.driveTime || ''}
              onChange={handleChange('driveTime')}
              fullWidth
              type="number"
            />
            <FormControl fullWidth>
              <InputLabel id="tour-step-driveTimeMeasure-label">
                {translate('jhipsterSampleApplicationApp.tourStep.driveTimeMeasure')}
              </InputLabel>
              <Select
                labelId="tour-step-driveTimeMeasure-label"
                id="tour-step-driveTimeMeasure"
                name="driveTimeMeasure"
                data-cy="driveTimeMeasure"
                value={formData.driveTimeMeasure || 'MINUTES'}
                label={translate('jhipsterSampleApplicationApp.tourStep.driveTimeMeasure')}
                onChange={handleSelectChange('driveTimeMeasure')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {durationMeasureValues.map(durationMeasure => (
                  <MenuItem value={durationMeasure} key={durationMeasure}>
                    {translate('jhipsterSampleApplicationApp.DurationMeasure.' + durationMeasure)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required error={!!errors.tour}>
              <InputLabel id="tour-step-tour-label">{translate('jhipsterSampleApplicationApp.tourStep.tour')}</InputLabel>
              <Select
                labelId="tour-step-tour-label"
                id="tour-step-tour"
                name="tour"
                data-cy="tour"
                value={formData.tour || ''}
                label={translate('jhipsterSampleApplicationApp.tourStep.tour')}
                onChange={handleSelectChange('tour')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {tours
                  ? tours.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="tour-step-place-label">{translate('jhipsterSampleApplicationApp.tourStep.place')}</InputLabel>
              <Select
                labelId="tour-step-place-label"
                id="tour-step-place"
                name="place"
                data-cy="place"
                value={formData.place || ''}
                label={translate('jhipsterSampleApplicationApp.tourStep.place')}
                onChange={handleSelectChange('place')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {places
                  ? places.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/tour-step"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TourStepUpdate;
